import { TenantCollectionHealth, TenantMailProcessingRow } from "./CoreAPI";
import { stalledQueues } from "./derive";

/**
 * A tenant's standing, worst first.
 *
 * The sibling of `MailboxStanding` in mailboxHealth.ts, and deliberately the
 * same shape: one function reduces every signal on the row to a single verdict,
 * and the table renders that verdict rather than any one field.
 *
 * WHY THIS EXISTS. The tenant dot used to render `collectionHealth` alone,
 * while the mailbox dot rendered a worst-of-everything standing. Same visual
 * language, two different meanings, one page apart. Observed on EMS_StarWars,
 * 2026-09-07: collection succeeded, so the dot was green and its accessible
 * name was "Healthy", while both of the tenant's mailboxes were unhealthy, an
 * outbound queue had been stalled for over two hours, and the row's own
 * `unhealthyCount` column read 2. The dot was faithful to its field and
 * misleading about its subject.
 *
 * TRUST PROBLEMS OUTRANK CONTENT FAULTS, which is why the three collection
 * states sit above `mailboxFault`. If collection is `Stale` then
 * `unhealthyCount` is stale too, so promoting the row to "mailboxes broken"
 * would assert something nobody re-measured. `collectionHealth` answers
 * "can you trust the rest of this row" (contract §2.4) and that question has to
 * be settled before the rest of the row is read.
 */
export type TenantStanding =
    | "collectionFailing"
    | "collectionStale"
    | "neverCollected"
    | "unknownHealth"
    | "mailboxFault"
    | "stalled"
    | "waiting"
    | "healthy"
    | "disabled";

/**
 * What each collection health settles on its own, or `undefined` to read on.
 *
 * A `Record` rather than a chain of `if`s for two reasons. It makes a sixth
 * `TenantCollectionHealth` fail to compile until someone decides whether it is
 * a trust problem or a fall-through — an `===` chain would have compiled clean
 * and quietly treated the new state as healthy. And it gives a runtime set of
 * the values this client knows, which is what makes `unknownHealth` detectable
 * at all.
 *
 * `Healthy` maps to `undefined` deliberately: it does not settle the row, it
 * only says the figures below can be believed.
 */
const HEALTH_STANDING: Record<TenantCollectionHealth, TenantStanding | undefined> = {
    Disabled: "disabled",
    Failing: "collectionFailing",
    Stale: "collectionStale",
    NeverCollected: "neverCollected",
    Healthy: undefined,
};

/**
 * Reduces a row to its worst finding.
 *
 * CHECK ORDER MATTERS, and the first check is the one that is easy to get
 * wrong — the same trap mailboxStanding documents. A tenant whose collection is
 * switched off in configuration keeps whatever figures were stored when it was
 * last switched on, quite possibly a fault. Testing faults before enablement
 * would therefore mark a deliberately-disabled tenant as broken forever.
 *
 * `lastOutcome` is deliberately NOT consulted. `LockTimeout` there means another
 * collector node held the lock, so that tick measured nothing (contract §2.5) —
 * it is not a failure, and `collectionHealth` already accounts for a genuinely
 * failed run. Reading both would double-count one fact.
 *
 * `terminal` is deliberately NOT consulted either. Those counts only ever
 * accumulate, so a dot driven by them would turn amber on a tenant's first
 * failed email and never return to green — an alarm that never clears is an
 * alarm operators learn to ignore. mailboxStanding excludes them for the same
 * reason. They are reported by the table's own columns and by the attention
 * strip, which is where a cumulative figure belongs.
 *
 * An ABSENT `collectionHealth` falls through rather than short-circuiting:
 * history rows never carry the field at all (contract §2.2) and this function is
 * shared with that view. Absence is not a fault and not a claim of health, so
 * the row's own figures decide.
 */
export function tenantStanding(row: TenantMailProcessingRow): TenantStanding {
    if (row.collectionHealth !== undefined) {
        if (!(row.collectionHealth in HEALTH_STANDING)) {
            return "unknownHealth";
        }
        const settled = HEALTH_STANDING[row.collectionHealth];
        if (settled) {
            return settled;
        }
    }

    // An unreported count is not zero — but it is also not evidence of a fault,
    // so it cannot promote the tenant on the strength of a measurement nobody
    // made. Same guard mailboxStanding puts on `lastBacklog`.
    if (row.mailboxes?.unhealthyCount !== undefined && row.mailboxes.unhealthyCount > 0) {
        return "mailboxFault";
    }

    // Server-computed. Never re-derived from a timestamp (contract §2.3).
    if (stalledQueues(row).length > 0) {
        return "stalled";
    }

    if (row.mailboxes?.backlog !== undefined && row.mailboxes.backlog > 0) {
        return "waiting";
    }

    return "healthy";
}
